import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Label} from '@/components/ui/label/Label'
import {RadioBoxItem} from '@/components/ui/radio-box-atoms/RadioBoxAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import {formatDate} from '@/dayjs'
import {useGetPatient} from '@/features/patients/services/useGetPatient'
import {useGetPatientLastInfo} from '@/features/patients/services/useGetPatientLastInfo'
import {PatientLastInfo} from '@/features/patients/types'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {CreateProjectUrlParams} from '@/features/projects/types'
import {projectSiarRenewalToLabel} from '@/features/projects/utils'
import {TreatmentRequestType} from '@/features/treatment-requests/types'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {useTranslation} from '@/translations/i18n'
import {ObjectKeys} from '@/utilities/helpers'
import {Root} from '@radix-ui/react-toggle-group'
import {useController, useFormContext} from 'react-hook-form'
import {z} from 'zod'

const ProjectSiarFieldsSchema = ProjectFieldsSchema.pick({
    editingOperation: true,
    startDate: true,
    siarProject: true,
    patient: true,
    projectType: true,
    referringProject: true
})
type ProjectSiarFieldsSchema = z.infer<typeof ProjectSiarFieldsSchema>

export const ProjectSiarFields = () => {
    const {t} = useTranslation()
    const {control, reset} = useFormContext<ProjectSiarFieldsSchema>()
    const {field, fieldState} = useController({control, name: 'siarProject'})
    const {searchParams} = useParsedSearchParams(CreateProjectUrlParams)

    const {
        data: patient,
        isLoading: isLoadingPatient,
        isError: isErrorPatient
    } = useGetPatient({
        patientId: searchParams.patientId ?? undefined,
        responseType: 'extended',
        options: {
            enabled: !!searchParams.patientId
        }
    })

    const {
        data: lastInfo,
        isLoading: isLoadingLastInfo,
        isError: isErrorLastInfo
    } = useGetPatientLastInfo({
        patientId: searchParams.patientId ?? undefined,
        type: patient?.treatmentRequest?.type as 'cycle_resumption' | 'treatment_resumption',
        options: {
            enabled: !!searchParams.patientId
        }
    })

    const handleProjectSiarChange = (value: 'same_as_previous' | 'empty') => {
        if (!value) return

        field.onChange(value)
        if (value == 'same_as_previous') {
            reset()
        }
    }

    if (isLoadingPatient || isLoadingLastInfo) {
        return <Skeleton height={24} width={100} />
    }

    if (isErrorPatient || isErrorLastInfo) {
        return (
            <Text palette="danger" shade="700" size="textSm">
                {t('errors:default')}
            </Text>
        )
    }

    const renderLastProjectInfo = (
        treatmentRequestType?: TreatmentRequestType,
        lastProject?: PatientLastInfo['project']
    ) => {
        if (!treatmentRequestType) return null

        if (treatmentRequestType == 'new_takeover') {
            return (
                <Text palette="danger" shade="700" size="textSm">
                    {t('treatment_requests:type_error')}
                </Text>
            )
        }

        if (treatmentRequestType == 'cycle_resumption' && lastProject) {
            return (
                <Flexbox direction="column" gap={1.5} fullWidth>
                    <Text palette="neutral" shade="500" size="textSm" weight="500">
                        {t('projects:resume_cycle_last_project')}
                    </Text>
                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                        {t('projects:code_date', {
                            code: lastProject.code,
                            date: formatDate(lastProject.startDate, 'DD/MM/YYYY')
                        })}
                    </Text>
                </Flexbox>
            )
        }

        return treatmentRequestType == 'treatment_resumption' && lastProject ? (
            <Flexbox direction="column" gap={1.5} fullWidth>
                <Text palette="neutral" shade="500" size="textSm" weight="500">
                    {t('projects:resume_project_last_project')}
                </Text>
                <Text palette="neutral" shade="950" size="textSm" weight="500">
                    {t('projects:code_date', {
                        code: lastProject.code,
                        date: formatDate(lastProject.startDate, 'DD/MM/YYYY')
                    })}
                </Text>
            </Flexbox>
        ) : null
    }

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            <Flexbox direction="column" gap={1.5} fullWidth>
                <Label>{t('projects:siar_project')}</Label>
                <Root
                    type="single"
                    value={field.value || null}
                    onValueChange={handleProjectSiarChange}
                    style={{width: '100%'}}
                >
                    <Grid columns={2} gap={4} fullWidth>
                        {ObjectKeys(projectSiarRenewalToLabel).map(key => (
                            <GridItem fullWidth key={key}>
                                <RadioBoxItem name={field.name} value={key} error={!!fieldState.error?.message}>
                                    {t(projectSiarRenewalToLabel[key])}
                                </RadioBoxItem>
                            </GridItem>
                        ))}
                    </Grid>
                </Root>
            </Flexbox>
            {lastInfo &&
                field.value == 'same_as_previous' &&
                renderLastProjectInfo(patient?.treatmentRequest?.type, lastInfo.project)}
        </Flexbox>
    )
}
